import { type DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FileDto } from '@/api/api.dto';
import { getServiceId } from '@/api/tokens.helper';
import { Modal } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import type {
  CreateTaskPayload,
  TaskSubmissionPayload,
} from '@/components/CreateSheet/CreateEditTaskSheet';
import type { CreateEventPayload } from '@/components/CreateSheet/EditEventSheet/EditEventSheet';
import SegmentedControl from '@/components/SegmentedControl/SegmentedControl';
import { TagPicker } from '@/components/TagPicker/TagPicker';
import type { InviteEmailTemplate } from '@/store/auth/auth.dto';
import { useAuth } from '@/store/auth/auth.store';
import {
  type EventTagsDraft,
  generateEventCover,
  type PlanCrewMember,
  type PlanItineraryItem,
  type PlanTaskItem,
  refineEventPlan,
  runPlanGeneration,
  suggestEventTags,
} from '@/store/events/event-ai.api';
import {
  buildEventPatch,
  eventToSeed,
  packLocation,
  withCrewSelection,
} from '@/store/events/eventPatch';
import {
  type EventParticipant,
  EventParticipantType,
  type EventResponse,
  EVENTS,
  partyHeadcount,
  type SubEventInput,
  sumHeadcount,
  UNSPECIFIED_LOCATION,
} from '@/store/events/events.dto';
import { useEvents, useGuestSuggestionsQuery } from '@/store/events/events.store';
import {
  type GuestInvitee,
  pendingGuestInvites,
  useCreateInvitationMutation,
  useEventInviteAnswersQuery,
  useEventInvitesQuery,
  useInviteGuestsMutation,
} from '@/store/invites/invites.store';
import { useAwayLabels } from '@/store/leaves/leaves.store';
import { mapPeopleForSelection } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import {
  type TaskParticipant,
  TaskParticipantType,
  TaskPriority,
  TaskStatus,
} from '@/store/tasks/tasks.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { doDateRangesOverlap } from '@/utils/dateRangeUtils';
import { offsetDate, snapToNextSlot } from '@/utils/dateSlots';
import { STOP_SEP, stopLabel } from '@/utils/eventStops';
import { isOwnerGroupRole, roleToParticipantType, UserRole } from '@/utils/roles';
import { runWithRetry } from '@/utils/runWithRetry';
import { toast } from '@/utils/toast';

import '@/design-system/animations.scss';
import { EDIT_STEPS, STEPS } from './eventWizard.constants';
import type {
  AiChatMessage,
  AiCover,
  CrewMember,
  EventEditStep,
  EventGuest,
  Guest,
  ItineraryItem,
  ItinerarySuggestion,
  LocationStop,
  PlanTab,
  TaskItem,
  TaskSuggestion,
  WizardSeed,
} from './eventWizard.types';
import { dataUriToFile, fmtDate, fmtTime, fmtWhen, toAllDayRange } from './eventWizard.utils';
import s from './EventWizardModal.module.scss';
import { ItineraryTab } from './plan/ItineraryTab';
import { TasksTab } from './plan/TasksTab';
import { StepAudience } from './steps/StepAudience';
import { StepDetails } from './steps/StepDetails';
import { StepLocation } from './steps/StepLocation';
import { StepPlan } from './steps/StepPlan';
import { StepReview } from './steps/StepReview';
import { WizardStepper } from './WizardStepper';

interface EventWizardModalProps {
  open: boolean;
  onClose: () => void;
  /** Prefill applied on each open — "plan an event like this" hands over a past event. */
  seed?: WizardSeed;
  /**
   * An existing event turns the wizard into an editor: it opens on `editStep`
   * alone, prefilled from the event, and saves that one step rather than walking
   * to a review and creating something new.
   */
  event?: EventResponse;
  /** Which step the editor opens on. Ignored without `event`. */
  editStep?: EventEditStep;
  /**
   * The event that was just created, once it exists — for callers that want to
   * go and look at it. Fires after the wizard has closed itself.
   */
  onCreated?: (id: string) => void;
}

export function EventWizardModal({
  open,
  onClose,
  seed,
  event,
  editStep = 'details',
  onCreated,
}: EventWizardModalProps) {
  const isEdit = !!event;
  const editStepIndex = Math.max(
    0,
    STEPS.findIndex((entry) => entry.id === EDIT_STEPS[editStep].step),
  );
  const defaultEventType: string = EVENTS.GENERAL;
  const idRef = useRef(0);
  const nextId = () => (idRef.current += 1);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [pickerActive, setPickerActive] = useState(false);
  const logoPickerRef = useCallback((input: HTMLInputElement | null) => {
    logoInputRef.current = input;
    if (input) {
      // The picker's input stays mounted, so this hears every pick, not only the first.
      input.addEventListener('change', () => setPickerActive(true));
    }
  }, []);

  const [step, setStep] = useState(1);
  const [furthest, setFurthest] = useState(1);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [aiCoverLoading, setAiCoverLoading] = useState(false);
  const [aiCover, setAiCover] = useState<AiCover | null>(null);
  const [aiCoverError, setAiCoverError] = useState<string | null>(null);
  // Editing: the cover last saved here. `event` is the copy the wizard was opened with and keeps
  // the old cover, so this one is shown instead once a new cover is in — Retry included.
  const [savedCover, setSavedCover] = useState<{ id: string; image: FileDto | null } | null>(null);

  const [editItinId, setEditItinId] = useState<number | null>(null);
  const [editItinText, setEditItinText] = useState('');
  const [editTaskId, setEditTaskId] = useState<number | null>(null);
  const [editTaskText, setEditTaskText] = useState('');
  const [photos, setPhotos] = useState<{ id: number; url: string }[]>([]);

  const [locationQuery, setLocationQuery] = useState('');
  const [stops, setStops] = useState<LocationStop[]>([]);
  const locations = useMemo(() => stops.map((stop) => stop.name), [stops]);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  const [inviteEmailTemplate, setInviteEmailTemplate] = useState<InviteEmailTemplate>('classic');
  const [inviteNote, setInviteNote] = useState('');
  const [allDay, setAllDay] = useState(false);

  const [teamOpen, setTeamOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  // Guests of past events picked from "From recent events" — invited by person id,
  // the same invitation a contact gets, only without an email or phone.
  const [pastGuestsSelected, setPastGuestsSelected] = useState<string[]>([]);
  // Seed requests that need data the wizard loads after opening (people, past
  // guests) or a filled-in title; each clears itself once applied.
  const [pendingPastGuests, setPendingPastGuests] = useState(0);
  const [pendingPlan, setPendingPlan] = useState(false);
  const [pendingCover, setPendingCover] = useState(false);
  // Null on create — the backend seats every owner and crew member but `excludedCrew`,
  // and an exclusion list keeps whoever loads late in. Editing manages who of them is
  // on the event, since they can be taken off and added back.
  const [teamSelected, setTeamSelected] = useState<string[] | null>(null);
  const [excludedCrew, setExcludedCrew] = useState<string[]>([]);

  const [inviteName, setInviteName] = useState('');
  const [inviteContact, setInviteContact] = useState('');

  const [itineraryMode, setItineraryMode] = useState('');
  const [itinerary, setItinerary] = useState<ItineraryItem[]>([]);
  const [itDate, setItDate] = useState<Date | null>(null);
  const [itEndDate, setItEndDate] = useState<Date | null>(null);
  const [itActivity, setItActivity] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<ItinerarySuggestion[]>([]);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPromptDraft, setAiPromptDraft] = useState('');
  const [aiChat, setAiChat] = useState<AiChatMessage[]>([]);
  const [aiThinking, setAiThinking] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(true);
  const aiChatRef = useRef<HTMLDivElement>(null);
  const [showItineraryDetails, setShowItineraryDetails] = useState(false);
  const [itDetailActivity, setItDetailActivity] = useState('');
  const [itDetailDate, setItDetailDate] = useState<Date | null>(null);
  const [itDetailEndDate, setItDetailEndDate] = useState<Date | null>(null);
  const [itDetailLocation, setItDetailLocation] = useState('');
  const [itDetailNotes, setItDetailNotes] = useState('');

  const [tasksMode, setTasksMode] = useState('');
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState('');
  const [quickTaskDate, setQuickTaskDate] = useState<Date | null>(null);
  const [quickTaskEndDate, setQuickTaskEndDate] = useState<Date | null>(null);
  const [tasksAiLoading, setTasksAiLoading] = useState(false);
  const [tasksAiSuggestions, setTasksAiSuggestions] = useState<TaskSuggestion[]>([]);
  const [tasksAiPanelOpen, setTasksAiPanelOpen] = useState(false);
  const [tasksAiPromptDraft, setTasksAiPromptDraft] = useState('');
  const [tasksAiChat, setTasksAiChat] = useState<AiChatMessage[]>([]);
  const [tasksAiThinking, setTasksAiThinking] = useState(false);
  const [tasksAiChatOpen, setTasksAiChatOpen] = useState(true);
  const tasksAiChatRef = useRef<HTMLDivElement>(null);

  const [planTab, setPlanTab] = useState<PlanTab>('itinerary');

  const [eventType, setEventType] = useState(defaultEventType);

  const [creating, setCreating] = useState(false);
  // Tags: the AI suggests them when the review opens; once the user edits the
  // chips they are the user's and the AI no longer overwrites them.
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagsTouched, setTagsTouched] = useState(false);
  const [tagsAi, setTagsAi] = useState<'idle' | 'loading' | 'ok' | 'off'>('idle');
  const tagsTouchedRef = useRef(false);
  const tagsDraftRef = useRef<string | null>(null);
  const tagsAbortRef = useRef<AbortController | null>(null);
  const [outOfWaterEvents, setOutOfWaterEvents] = useState<EventResponse[]>([]);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const planAbortRef = useRef<AbortController | null>(null);

  const vesselId = getServiceId();
  const userId = useAuth((state) => state.session?.id);
  const inviteGuestsMutation = useInviteGuestsMutation();
  const createInvitation = useCreateInvitationMutation();
  const vessel = useVessels((state) => state.vessel);
  const { people, getPeople, hasFetchedPeople } = usePerson();
  const { createEvent, updateEvent, uploadCoverImage, fetchEvents } = useEvents();
  const guestSuggestions = useGuestSuggestionsQuery(open && !event);
  // activity names a past guest from the participant row, and older rows were
  // saved without one; the event API then refuses the whole event over a blank
  // participant name. Fill the gap from the vessel's people, drop whoever still
  // has no name, and keep one row per person (the service groups by person AND
  // name, so a renamed guest comes back twice).
  const pastGuests = useMemo(() => {
    const names = new Map(
      mapPeopleForSelection(people).map((p) => [p.id, `${p.firstName} ${p.lastName}`.trim()]),
    );
    const seen = new Set<string>();
    return (guestSuggestions.data ?? []).flatMap((guest) => {
      const name = guest.name.trim() || names.get(guest.personId) || '';
      if (!name || seen.has(guest.personId)) return [];
      seen.add(guest.personId);
      return [{ ...guest, name }];
    });
  }, [guestSuggestions.data, people]);

  useEffect(() => {
    if (open) void getPeople();
  }, [open, getPeople]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchEvents(1000, 1, undefined, undefined, undefined, undefined, EVENTS.OUT_OF_WATER)
      .then((res) => {
        if (!cancelled) setOutOfWaterEvents(res.items);
      })
      .catch(() => {
        if (!cancelled) setOutOfWaterEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fetchEvents]);

  const prevOpenRef = useRef(false);
  const seededRef = useRef(false);
  const prevStartRef = useRef<Date | null>(null);

  // A fresh open starts clean, then takes the seed: without the reset the wizard
  // would reopen holding whatever the last, abandoned run had typed into it.
  useEffect(() => {
    if (open === prevOpenRef.current) return;
    prevOpenRef.current = open;
    if (!open) return;
    resetAll();
    seededRef.current = false;
    prevStartRef.current = null;
    // Editing lands on its own step, with the event itself as the prefill.
    if (event) {
      setTeamSelected(
        (event.participants ?? [])
          .filter((p) => p.type !== EventParticipantType.GUEST)
          .map((p) => p.person_id),
      );
      setEventType(event.type);
      setStep(editStepIndex + 1);
      setFurthest(editStepIndex + 1);
      setPlanTab(EDIT_STEPS[editStep].planTab ?? 'itinerary');
    }
    const prefill: WizardSeed | undefined = seed ?? (event ? eventToSeed(event) : undefined);
    if (!prefill) return;
    setTitle(prefill.title ?? '');
    setDescription(prefill.description ?? '');
    setMoreDetailsOpen(!!prefill.description);
    setStops((prefill.locations ?? []).map((name) => ({ id: nextId(), name })));
    setStartDate(prefill.startDate ?? null);
    setEndDate(prefill.endDate ?? null);
    setAllDay(prefill.allDay ?? false);
    // A copied guest list is only useful if it is on screen: the accordion opens
    // so the rows can be trimmed before they turn into invitations.
    if (prefill.guests?.length) {
      setGuests(prefill.guests.map((g) => ({ ...g, id: nextId() })));
      setGuestsOpen(true);
    }
    setItinerary(
      (prefill.itinerary ?? []).map((it) => ({
        id: nextId(),
        activity: it.activity,
        start: it.start ?? null,
        end: it.end ?? null,
        source: it.source,
      })),
    );
    setTasks(
      (prefill.tasks ?? []).map((t) => ({
        id: nextId(),
        title: t.title,
        start: t.start ?? null,
        end: t.end ?? null,
        private: !!t.private,
      })),
    );
    // A seeded plan is already the user's own work — 'manual' keeps the plan step
    // from firing AI generation over the top of it.
    if (prefill.itinerary?.length) setItineraryMode('manual');
    if (prefill.tasks?.length) setTasksMode('manual');
    if (prefill.eventType) setEventType(prefill.eventType);
    if (prefill.pastGuests) setPendingPastGuests(prefill.pastGuests);
    if (prefill.autoPlan) setPendingPlan(true);
    if (prefill.autoCover) setPendingCover(true);
    seededRef.current = true;
    prevStartRef.current = prefill.startDate ?? prefill.planAnchor ?? null;
    // resetAll is a stable function declaration; the seed is read once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Moving day one moves the seeded plan with it: a copied itinerary's dates only
  // mean anything relative to the start.
  // Cleared dates keep the last start, so picking new ones still moves the plan from it.
  useEffect(() => {
    if (!seededRef.current || !startDate) return;
    const previousStart = prevStartRef.current;
    prevStartRef.current = startDate;
    if (!previousStart) return;
    const delta = startDate.getTime() - previousStart.getTime();
    if (!delta) return;
    const shift = (d: Date | null | undefined) => (d ? new Date(d.getTime() + delta) : null);
    setItinerary((items) =>
      items.map((it) => ({ ...it, start: shift(it.start), end: shift(it.end) })),
    );
    setTasks((items) => items.map((t) => ({ ...t, start: shift(t.start), end: shift(t.end) })));
  }, [startDate]);

  // Who is on leave over the event's days, marked and listed last on the audience step.
  const away = useAwayLabels(startDate, endDate);

  const crewMembers: CrewMember[] = useMemo(
    () =>
      mapPeopleForSelection(people)
        .filter((p) => p.role !== UserRole.GUEST)
        .map((p) => ({
          id: p.id,
          name: `${p.firstName} ${p.lastName}`.trim() || 'Crew member',
          role: p.role ?? '',
        })),
    [people],
  );
  // The guests already on the event being edited, whether they registered or only answered.
  const { data: eventInvites } = useEventInvitesQuery(event?.id, event?.vessel_id);
  const { data: eventAnswers } = useEventInviteAnswersQuery(event?.id, event?.vessel_id);
  const eventGuests: EventGuest[] = useMemo(() => {
    if (!event) return [];
    const names = new Map(
      mapPeopleForSelection(people).map((p) => [p.id, `${p.firstName} ${p.lastName}`.trim()]),
    );
    const participants = (event.participants ?? []).filter(
      (p) => p.type === EventParticipantType.GUEST,
    );
    const answers = eventAnswers ?? [];
    return [
      ...participants.map(
        (p): EventGuest => ({
          key: p.person_id,
          name: names.get(p.person_id) || p.name || 'Guest',
          status: p.status === 'accepted' || p.status === 'declined' ? p.status : 'invited',
          declineReason: p.decline_reason,
          additionalGuests: p.additional_guests ?? 0,
        }),
      ),
      ...answers.map(
        (a): EventGuest => ({
          key: a.id,
          name: a.name || a.contact,
          status: a.accepted ? 'accepted' : 'declined',
          declineReason: a.declineReason,
          additionalGuests: a.additionalGuests,
        }),
      ),
      ...pendingGuestInvites(
        eventInvites?.items,
        new Set(participants.map((p) => p.person_id)),
        answers,
      ).map(
        (i): EventGuest => ({
          key: i.id,
          name: i.name || i.contact,
          status: 'invited',
          additionalGuests: i.additionalGuests ?? 0,
        }),
      ),
    ];
  }, [event, people, eventInvites, eventAnswers]);

  const attendingCrew = useMemo(
    () =>
      crewMembers.filter((m) =>
        teamSelected ? teamSelected.includes(m.id) : !excludedCrew.includes(m.id),
      ),
    [crewMembers, teamSelected, excludedCrew],
  );
  // const eventDurationDays =
  //   startDate && endDate
  //     ? Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
  //     : 1;
  // const exceedsMaxDays = eventDurationDays > MAX_EVENT_DAYS;

  // An event without dates is an "idea" (events.dto.ts isIdeaEvent). The plan step still
  // shows so the itinerary and tasks can be filled in by hand, but with no range to
  // schedule against there is nothing to feed the generator: manual only.
  const canGeneratePlan = !!startDate;
  // `step` is a 1-based index into STEPS; one past the end is the review.
  const reviewStep = STEPS.length + 1;
  const currentStep = STEPS[step - 1];

  // In people: each guest counts with the guests they bring.
  // A past guest comes back with the party they brought last time.
  const guestCount =
    sumHeadcount(guests, partyHeadcount) +
    sumHeadcount(
      pastGuests.filter((g) => pastGuestsSelected.includes(g.personId)),
      partyHeadcount,
    );
  const ownerCount = attendingCrew.filter((m) => isOwnerGroupRole(m.role)).length;
  const audienceSummary = [
    ownerCount ? `${ownerCount} owner${ownerCount !== 1 ? 's' : ''}` : null,
    attendingCrew.length > ownerCount ? `${attendingCrew.length - ownerCount} crew` : null,
    guestCount ? `${guestCount} guest${guestCount !== 1 ? 's' : ''}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const reviewItems = [
    { label: 'Event', value: title.trim() || 'Untitled event' },
    {
      label: 'Location',
      value: locations.length ? `📍 ${locations.join(' · ')}` : 'Not specified',
    },
    {
      label: 'Date & Time',
      value: startDate
        ? allDay
          ? `${fmtDate(startDate)}${endDate && endDate.toDateString() !== startDate.toDateString() ? ` → ${fmtDate(endDate)}` : ''} · All day`
          : `${fmtDate(startDate)} · ${fmtTime(startDate)}${
              endDate
                ? ` → ${endDate.toDateString() !== startDate.toDateString() ? `${fmtDate(endDate)} · ` : ''}${fmtTime(endDate)}`
                : ''
            }`
        : 'No dates yet · saved as an idea',
    },
    { label: 'Audience', value: audienceSummary || 'Not set' },
    {
      label: 'Itinerary',
      value: itinerary.length
        ? `${itinerary.length} item${itinerary.length !== 1 ? 's' : ''}`
        : 'None',
    },
    {
      label: 'Team Tasks',
      value: tasks.length ? `${tasks.length} task${tasks.length !== 1 ? 's' : ''}` : 'None',
    },
    {
      label: 'Photos',
      value: photos.length ? `${photos.length} photo${photos.length !== 1 ? 's' : ''}` : 'None',
    },
  ];
  const isSkippable = !!currentStep && !currentStep.required;

  const outOfWaterRange = useMemo(() => {
    if (!startDate) return null;
    const selectedEnd = endDate ?? startDate;
    const match = outOfWaterEvents.find(
      (event) =>
        !!event.time_slot &&
        doDateRangesOverlap(startDate, selectedEnd, event.time_slot.start, event.time_slot.end),
    );
    if (!match || !match.time_slot) return null;
    return { start: new Date(match.time_slot.start), end: new Date(match.time_slot.end) };
  }, [startDate, endDate, outOfWaterEvents]);

  const saveCover = async (id: string, file: File) =>
    setSavedCover({ id, image: await uploadCoverImage(id, file) });

  /** `quiet` is the automatic run on open: a failed cover is simply no cover. */
  const generateAiCover = async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!title.trim()) return;
    setAiCoverLoading(true);
    setAiCoverError(null);
    try {
      const res = await generateEventCover({
        vessel_id: vesselId,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || undefined,
        event_type: eventType || undefined,
      });
      setAiCover({ base64: res.image_base64, contentType: res.content_type });
      setPhoto(null);
      // Editing: a generated cover is saved as soon as it is chosen, like a picked one. Until it
      // is in, the generating state stays up; if it fails it is not shown as the cover.
      if (event) {
        const file = await dataUriToFile(res.image_base64, res.content_type, 'ai-cover.webp');
        if (!(await runWithRetry(() => saveCover(event.id, file)))) setAiCover(null);
      }
    } catch (error) {
      if (quiet) {
        console.error('Automatic AI cover failed:', error);
        return;
      }
      setAiCoverError(
        error instanceof Error ? error.message : 'Couldn’t generate the cover. Please try again.',
      );
    } finally {
      setAiCoverLoading(false);
    }
  };

  // A place may repeat (a round trip ends where it started), just not back to back:
  // that is a zero-length leg, and parseStops folds it into one stop on reload anyway.
  // Every edit to the list keeps it that way.
  const backToBack = (list: LocationStop[]) =>
    list.find((stop, i) => i > 0 && list[i - 1].name === stop.name);
  const setStopsUnlessBackToBack = (next: LocationStop[]) => {
    const repeat = backToBack(next);
    if (repeat) {
      toast.info(`${stopLabel(repeat.name)} can’t be two stops in a row`, { id: 'repeat-stop' });
    } else {
      setStops(next);
    }
  };

  const addLocation = (value: string | null, hint?: LocationStop['hint']) => {
    const place = (value ?? '').trim();
    if (place) setStopsUnlessBackToBack([...stops, { id: nextId(), name: place, hint }]);
    setLocationQuery('');
  };
  // Removing the only stop between two visits to a place leaves them back to back: they merge.
  const removeLocation = (id: number) => {
    const rest = stops.filter((l) => l.id !== id);
    const merged = backToBack(rest);
    if (merged) toast.info(`The two ${stopLabel(merged.name)} stops are now one`);
    setStops(rest.filter((stop) => stop !== merged));
  };

  const handleLocationDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = stops.findIndex((l) => l.id === active.id);
    const newIndex = stops.findIndex((l) => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setStopsUnlessBackToBack(arrayMove(stops, oldIndex, newIndex));
  };

  // The invite form is always on screen; it clears its own open tab after sending.
  const clearInvite = () => {
    setInviteName('');
    setInviteContact('');
  };
  // The invite form hands over whom its open tab names: the picked contacts, or the one
  // guest typed in by name and contact.
  const sendInvitation = (recipients: GuestInvitee[]) => {
    if (!recipients.length) return;
    // A contact who is also a ticked past guest is invited once, as the contact.
    setPastGuestsSelected((prev) =>
      prev.filter((id) => !recipients.some((p) => p.personId === id)),
    );
    setGuests((prev) => [
      ...prev,
      ...recipients.map((guest) => ({
        id: nextId(),
        ...guest,
        contactType: guest.contact.includes('@') ? ('email' as const) : ('phone' as const),
      })),
    ]);
  };
  const removeGuest = (id: number) => setGuests((prev) => prev.filter((g) => g.id !== id));

  const toggleId = (prev: string[], id: string) =>
    prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id];
  const toggleTeam = (id: string) => {
    if (teamSelected) setTeamSelected(toggleId(teamSelected, id));
    else if (id !== userId) setExcludedCrew((prev) => toggleId(prev, id));
  };
  const addAllTeam = () => {
    if (teamSelected)
      setTeamSelected([...new Set([...teamSelected, ...crewMembers.map((m) => m.id)])]);
    else setExcludedCrew([]);
  };

  const addItineraryItem = () => {
    const activity = itActivity.trim();
    if (!activity) return;
    setItinerary((prev) => [...prev, { id: nextId(), activity, start: itDate, end: itEndDate }]);
    setItDate(null);
    setItEndDate(null);
    setItActivity('');
  };
  const removeItineraryItem = (id: number) =>
    setItinerary((prev) => prev.filter((i) => i.id !== id));

  const openItineraryDetails = () => setShowItineraryDetails(true);
  const closeItineraryDetails = () => {
    setShowItineraryDetails(false);
    setItDetailActivity('');
    setItDetailDate(null);
    setItDetailEndDate(null);
    setItDetailLocation('');
    setItDetailNotes('');
  };
  const saveItineraryDetails = () => {
    const activity = itDetailActivity.trim();
    if (!activity) return;
    setItinerary((prev) => [
      ...prev,
      { id: nextId(), activity, start: itDetailDate, end: itDetailEndDate },
    ]);
    closeItineraryDetails();
  };

  const mapItinerarySuggestions = (items: PlanItineraryItem[]) =>
    items.map((it) => ({
      id: nextId(),
      time: it.start ? fmtWhen(new Date(it.start)) : '',
      activity: it.activity,
      start: it.start ? new Date(it.start) : null,
      end: it.end ? new Date(it.end) : null,
    }));
  const mapTaskSuggestions = (items: PlanTaskItem[]) =>
    items.map((t) => ({
      id: nextId(),
      title: t.title,
      start: t.start ? new Date(t.start) : null,
      end: t.end ? new Date(t.end) : null,
      private: false,
    }));

  const runPlan = async () => {
    setItineraryMode('ai');
    setTasksMode('ai');
    setAiLoading(true);
    setTasksAiLoading(true);
    setAiPanelOpen(false);
    setTasksAiPanelOpen(false);
    setPlanError(null);
    setAiChat([]);
    setTasksAiChat([]);
    planAbortRef.current?.abort();
    const controller = new AbortController();
    planAbortRef.current = controller;
    try {
      const crew: PlanCrewMember[] = attendingCrew.map((m) => ({
        person_id: m.id,
        name: m.name,
        role: m.role,
      }));
      const result = await runPlanGeneration(
        {
          vessel_id: vesselId,
          user_id: userId,
          event: {
            title: title.trim(),
            description: description.trim() || undefined,
            event_type: eventType || undefined,
            start: (startDate ?? new Date()).toISOString(),
            end: endDate ? endDate.toISOString() : undefined,
            locations,
            crew,
            guests_count: guestCount,
          },
        },
        controller.signal,
      );
      setSessionId(result.session_id);
      setAiSuggestions(mapItinerarySuggestions(result.itinerary));
      setTasksAiSuggestions(mapTaskSuggestions(result.tasks));
      setAiChat([
        {
          id: nextId(),
          role: 'ai',
          text: 'Here’s a starter draft based on your event details. Please tell me what to tweak and I’ll refine it.',
        },
      ]);
      setTasksAiChat([
        {
          id: nextId(),
          role: 'ai',
          text: 'Here’s a starter to-do list based on your event. Please tell me what to tweak and I’ll refine it.',
        },
      ]);
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return;
      setPlanError(
        error instanceof Error ? error.message : 'Couldn’t draft the plan. Please try again.',
      );
    } finally {
      if (planAbortRef.current === controller) planAbortRef.current = null;
      setAiLoading(false);
      setTasksAiLoading(false);
    }
  };

  const acceptSuggestion = (id: number) => {
    const sg = aiSuggestions.find((item) => item.id === id);
    if (!sg) return;
    setItinerary((prev) => [
      ...prev,
      { id: nextId(), time: sg.time, activity: sg.activity, start: sg.start, end: sg.end },
    ]);
    setAiSuggestions((prev) => prev.filter((item) => item.id !== id));
  };
  const rejectSuggestion = (id: number) =>
    setAiSuggestions((prev) => prev.filter((item) => item.id !== id));
  const acceptAllSuggestions = () => {
    setItinerary((prev) => [
      ...prev,
      ...aiSuggestions.map((sg) => ({
        id: nextId(),
        time: sg.time,
        activity: sg.activity,
        start: sg.start,
        end: sg.end,
      })),
    ]);
    setAiSuggestions([]);
  };
  const startEditItin = (id: number, text: string) => {
    setEditItinId(id);
    setEditItinText(text);
  };
  const saveEditItin = () => {
    if (editItinId == null) return;
    const text = editItinText.trim();
    if (text) {
      setAiSuggestions((prev) =>
        prev.map((sg) => (sg.id === editItinId ? { ...sg, activity: text } : sg)),
      );
    }
    setEditItinId(null);
    setEditItinText('');
  };
  const cancelEditItin = () => {
    setEditItinId(null);
    setEditItinText('');
  };
  const submitAiPrompt = async () => {
    const draft = aiPromptDraft.trim();
    if (!draft || !sessionId) return;
    setAiChat((prev) => [...prev, { id: nextId(), role: 'user', text: draft }]);
    setAiPromptDraft('');
    setAiThinking(true);
    try {
      const res = await refineEventPlan({
        session_id: sessionId,
        target: 'itinerary',
        message: draft,
      });
      setAiSuggestions(mapItinerarySuggestions(res.itinerary));
      setTasksAiSuggestions(mapTaskSuggestions(res.tasks));
      setAiChat((prev) => [...prev, { id: nextId(), role: 'ai', text: res.reply }]);
    } catch (error) {
      setAiChat((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'ai',
          text:
            error instanceof Error
              ? error.message
              : 'Couldn’t refine the itinerary. Please try again.',
        },
      ]);
    } finally {
      setAiThinking(false);
    }
  };

  useEffect(() => {
    if (aiChatOpen && aiChatRef.current) {
      aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
    }
  }, [aiChat, aiThinking, aiChatOpen]);

  // The task wizard is held to single tasks here (`hideRepeat`), so that is all it sends.
  const addTask = async (payload: TaskSubmissionPayload) => {
    if (payload.type !== 'single') return;
    const task = payload.data;
    setTasks((prev) => [
      ...prev,
      {
        id: nextId(),
        title: task.title,
        start: new Date(task.time_slot.start),
        end: new Date(task.time_slot.end),
        private: task.visibility === 'private',
        status: task.status as TaskStatus,
        payload: task,
      },
    ]);
  };
  const removeTask = (id: number) => setTasks((prev) => prev.filter((t) => t.id !== id));

  const addQuickTask = () => {
    const t = quickTaskTitle.trim();
    if (!t) return;
    setTasks((prev) => [
      ...prev,
      { id: nextId(), title: t, start: quickTaskDate, end: quickTaskEndDate, private: false },
    ]);
    setQuickTaskTitle('');
    setQuickTaskDate(null);
    setQuickTaskEndDate(null);
  };

  const openTaskForm = () => setShowTaskForm(true);
  const closeTaskForm = () => setShowTaskForm(false);

  const acceptTaskSuggestion = (id: number) => {
    const sg = tasksAiSuggestions.find((item) => item.id === id);
    if (!sg) return;
    if (!tasks.some((task) => task.title === sg.title)) {
      setTasks((prev) => [
        ...prev,
        { id: nextId(), title: sg.title, start: sg.start, end: sg.end, private: false },
      ]);
    }
    setTasksAiSuggestions((prev) => prev.filter((item) => item.id !== id));
  };
  const rejectTaskSuggestion = (id: number) =>
    setTasksAiSuggestions((prev) => prev.filter((item) => item.id !== id));
  const acceptAllTaskSuggestions = () => {
    const existing = new Set(tasks.map((t) => t.title));
    const additions = tasksAiSuggestions.filter((sg) => !existing.has(sg.title));
    setTasks((prev) => [
      ...prev,
      ...additions.map((sg) => ({
        id: nextId(),
        title: sg.title,
        start: sg.start,
        end: sg.end,
        private: false,
      })),
    ]);
    setTasksAiSuggestions([]);
  };
  const startEditTask = (id: number, text: string) => {
    setEditTaskId(id);
    setEditTaskText(text);
  };
  const saveEditTask = () => {
    if (editTaskId == null) return;
    const text = editTaskText.trim();
    setTasksAiSuggestions((prev) =>
      prev.map((sg) => (sg.id === editTaskId ? { ...sg, title: text || sg.title } : sg)),
    );
    setEditTaskId(null);
    setEditTaskText('');
  };
  const cancelEditTask = () => {
    setEditTaskId(null);
    setEditTaskText('');
  };
  const submitTasksAiPrompt = async () => {
    const draft = tasksAiPromptDraft.trim();
    if (!draft || !sessionId) return;
    setTasksAiChat((prev) => [...prev, { id: nextId(), role: 'user', text: draft }]);
    setTasksAiPromptDraft('');
    setTasksAiThinking(true);
    try {
      const res = await refineEventPlan({ session_id: sessionId, target: 'tasks', message: draft });
      setAiSuggestions(mapItinerarySuggestions(res.itinerary));
      setTasksAiSuggestions(mapTaskSuggestions(res.tasks));
      setTasksAiChat((prev) => [...prev, { id: nextId(), role: 'ai', text: res.reply }]);
    } catch (error) {
      setTasksAiChat((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'ai',
          text:
            error instanceof Error ? error.message : 'Couldn’t refine the tasks. Please try again.',
        },
      ]);
    } finally {
      setTasksAiThinking(false);
    }
  };

  useEffect(() => {
    if (tasksAiChatOpen && tasksAiChatRef.current) {
      tasksAiChatRef.current.scrollTop = tasksAiChatRef.current.scrollHeight;
    }
  }, [tasksAiChat, tasksAiThinking, tasksAiChatOpen]);

  useEffect(() => {
    if (currentStep?.id !== 'plan' || !canGeneratePlan) return;
    if (!itineraryMode && !tasksMode && !aiLoading && !tasksAiLoading) void runPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => () => planAbortRef.current?.abort(), []);

  // Seeded regulars → the top past guests who accepted before.
  useEffect(() => {
    if (!pendingPastGuests) return;
    if (guestSuggestions.isError) {
      setPendingPastGuests(0);
      return;
    }
    // Names come partly from the people list, so wait for it too.
    if (!guestSuggestions.data || !hasFetchedPeople) return;
    const regulars = pastGuests
      .filter((g) => g.acceptedCount > 0)
      .slice(0, pendingPastGuests)
      .map((g) => g.personId);
    if (regulars.length) {
      setPastGuestsSelected(regulars);
      setGuestsOpen(true);
    }
    setPendingPastGuests(0);
  }, [
    pendingPastGuests,
    guestSuggestions.data,
    guestSuggestions.isError,
    hasFetchedPeople,
    pastGuests,
  ]);

  // The automatic plan waits for crew and guests (both feed the prompt), but not
  // forever: a vessel whose people never load still gets its plan.
  useEffect(() => {
    if (!pendingPlan) return;
    const timer = window.setTimeout(() => setPendingPastGuests(0), 5000);
    return () => window.clearTimeout(timer);
  }, [pendingPlan]);

  useEffect(() => {
    if (!pendingPlan || !canGeneratePlan || pendingPastGuests) return;
    setPendingPlan(false);
    if (!itineraryMode && !tasksMode) void runPlan();
    // runPlan reads the state this render settled; it is not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPlan, canGeneratePlan, pendingPastGuests]);

  useEffect(() => {
    if (!pendingCover || !title.trim()) return;
    setPendingCover(false);
    void generateAiCover({ quiet: true });
    // generateAiCover reads the seeded title/description/type; it is not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCover, title]);

  const togglePastGuest = (personId: string) =>
    setPastGuestsSelected((prev) =>
      prev.includes(personId) ? prev.filter((id) => id !== personId) : [...prev, personId],
    );

  const buildParticipants = (): EventParticipant[] => [
    ...attendingCrew.map((m) => ({
      person_id: m.id,
      type: roleToParticipantType(m.role) as EventParticipantType,
      name: m.name,
    })),
  ];

  const buildSubEvents = (): SubEventInput[] =>
    itinerary.map((it) => {
      const start = it.start ?? startDate ?? new Date();
      const end = it.end ?? it.start ?? endDate ?? start;
      // A saved row goes back with its id, notes and people, so it updates in place.
      return {
        id: it.source?.id,
        title: it.activity,
        description: it.source?.description ?? '',
        time_slot: { start: start.toISOString(), end: end.toISOString() },
        participants: it.source?.participants ?? [],
      };
    });

  const buildTaskParticipants = (): TaskParticipant[] =>
    buildParticipants()
      .filter((p) => p.type !== EventParticipantType.GUEST)
      .map((p) => ({
        person_id: p.person_id,
        type:
          p.type === EventParticipantType.SERVICE_PROVIDER
            ? TaskParticipantType.SERVICE_PROVIDER
            : TaskParticipantType.CREW,
        name: p.name,
      }));

  const buildTasks = (): CreateTaskPayload[] =>
    tasks.map((t) => {
      const start = t.start ?? startDate ?? new Date();
      const end = t.end ?? t.start ?? endDate ?? start;
      const startIso = start.toISOString();
      const endIso = end.toISOString();
      // A task added through the task wizard carries what was entered there; quick-added
      // and AI rows are a title and dates. Title and dates come from the row either way:
      // the row can be renamed, and moving the event moves its tasks.
      const entered = t.payload;
      return {
        title: t.title,
        description: entered?.description ?? '',
        location: {
          name: UNSPECIFIED_LOCATION,
          address: '',
          latitude: 0,
          longitude: 0,
          map_url: '',
        },
        type: entered?.type ?? 'deadline_based',
        status: t.status ?? TaskStatus.PENDING,
        priority: entered?.priority ?? TaskPriority.MEDIUM,
        equipment: entered?.equipment ?? null,
        start: startIso,
        deadline: endIso,
        reminder: startIso,
        time_slot: { type: 'day_based', start: startIso, end: endIso },
        responsible: entered?.responsible ?? null,
        participants: t.private
          ? []
          : entered?.participants?.length
            ? entered.participants
            : buildTaskParticipants(),
        cost: entered?.cost,
        visibility: t.private ? 'private' : 'public',
      };
    });

  // Typed-in guests are invited by email or phone; picked contacts and past
  // guests by person id. All go through an invitation: accepting it is what
  // gives a guest the vessel role they need to open the event and answer it.
  const inviteGuests = async () => {
    const regulars = pastGuests.filter(
      (g) =>
        pastGuestsSelected.includes(g.personId) && !crewMembers.some((m) => m.id === g.personId),
    );
    if (guests.length === 0 && regulars.length === 0) return;

    const createdEvent = useEvents.getState().currentEvent;
    if (!createdEvent || !vessel) return;
    const invitedTo = { id: createdEvent.id, expires_at: inviteExpiresAt ?? undefined };

    const results = await Promise.allSettled([
      ...(guests.length > 0
        ? [
            inviteGuestsMutation.mutateAsync({
              invitees: guests,
              vessel,
              event: invitedTo,
              emailTemplate: inviteEmailTemplate,
              message: inviteNote.trim() || undefined,
            }),
          ]
        : []),
      ...regulars.map((g) =>
        createInvitation.mutateAsync({
          personId: g.personId,
          name: g.name,
          role: UserRole.GUEST,
          serviceId: vessel.id,
          serviceType: 'vessel',
          vesselName: vessel.vessel_name,
          event: invitedTo,
          message: inviteNote.trim() || undefined,
          additionalGuests: g.additionalGuests,
        }),
      ),
    ]);
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      console.error('Guest invitations failed:', failed);
      toast.error('Couldn’t send the invitations. Please try again.');
    }
  };

  const handleAllDayChange = (checked: boolean) => {
    setAllDay(checked);
    if (!startDate) return;
    if (checked) {
      const range = toAllDayRange(startDate, endDate);
      setStartDate(range.start);
      setEndDate(range.end);
      return;
    }
    const slot = snapToNextSlot();
    const start = new Date(startDate);
    start.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
    setStartDate(start);
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
      setEndDate(end.getTime() > start.getTime() ? end : offsetDate(start, 15));
    }
  };

  const buildTimeSlot = (type: string, requireEnd: boolean): CreateEventPayload['time_slot'] => {
    if (!startDate) return undefined;
    if (allDay) {
      const range = toAllDayRange(startDate, endDate);
      return {
        type,
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        all_day: true,
      };
    }
    const end = endDate ?? (requireEnd ? startDate : null);
    return {
      type,
      start: startDate.toISOString(),
      ...(end ? { end: end.toISOString() } : {}),
      all_day: false,
    };
  };

  const changeTags = (next: string[]) => {
    tagsTouchedRef.current = true;
    tagsAbortRef.current?.abort();
    setTagsTouched(true);
    setTagIds(next);
    setTagsAi((state) => (state === 'loading' ? 'idle' : state));
  };

  // On the review, ask the AI for tags — again if the user went back and changed
  // what they are read from, as long as the user has not edited them yet.
  useEffect(() => {
    // The vessel the event is created on — the same header createEvent sends.
    const vesselId = getServiceId();
    if (!open || isEdit || step < reviewStep || tagsTouched || !vesselId || !userId) return;
    const timeSlot = buildTimeSlot('day_based', false);
    const draft: EventTagsDraft = {
      vessel_id: vesselId,
      user_id: userId,
      title: title.trim(),
      description: description.trim() || undefined,
      type: eventType || undefined,
      location_name: locations[0],
      location_address: locations.length ? locations.join(STOP_SEP) : undefined,
      start: timeSlot?.start,
      end: timeSlot?.end,
      itinerary: itinerary.map((it) => it.activity).filter(Boolean),
    };
    const key = JSON.stringify(draft);
    if (key === tagsDraftRef.current) return;
    tagsDraftRef.current = key;

    tagsAbortRef.current?.abort();
    const controller = new AbortController();
    tagsAbortRef.current = controller;
    setTagsAi('loading');
    suggestEventTags(draft, controller.signal)
      .then((res) => {
        if (controller.signal.aborted || tagsTouchedRef.current) return;
        setTagIds(res.tags.map((tag) => tag.id));
        setTagsAi(res.status === 'ok' ? 'ok' : 'off');
      })
      .catch(() => {
        // Aborted: a newer request, an edit of the chips, or the wizard closed.
      });
    // buildTimeSlot reads the dates listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isEdit,
    step,
    reviewStep,
    tagsTouched,
    userId,
    title,
    description,
    eventType,
    locations,
    startDate,
    endDate,
    allDay,
    itinerary,
  ]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      let cover: File | null = photo;
      if (!cover && aiCover) {
        cover = await dataUriToFile(aiCover.base64, aiCover.contentType, 'ai-cover.webp');
      }
      const payload: CreateEventPayload = {
        participants: buildParticipants(),
        excluded_participants: excludedCrew.filter((id) => id !== userId),
        title: title.trim() || 'Untitled event',
        description: description.trim() || '',
        location: {
          name: locations[0] ?? UNSPECIFIED_LOCATION,
          address: locations.join(STOP_SEP),
          latitude: 0,
          longitude: 0,
          map_url: '',
        },
        type: (eventType || EVENTS.GENERAL) as string,
        // Undated events post no time_slot at all — that is what makes them an idea.
        time_slot: buildTimeSlot('day_based', false),
        cover_image: cover,
        sub_events: buildSubEvents(),
        tasks: buildTasks(),
        // The user saw the tags at the review (the AI's, or their own): saved as they
        // are, even none. Without that — the AI did not answer, the user did not
        // touch them — the tagger picks them after the save, as before.
        ...(tagsTouched || tagsAi === 'ok' ? { tag_ids: tagIds } : {}),
      };
      await createEvent(payload);
      // The store parks what it created in `currentEvent`, which is the only
      // place the new id shows up — `createEvent` itself returns nothing.
      const createdId = useEvents.getState().currentEvent?.id;
      await inviteGuests();
      toast.success('Your event has been added.');
      onClose();
      resetAll();
      // Last, so a caller that navigates away does it to a wizard already shut.
      if (createdId) onCreated?.(createdId);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Couldn’t add the event. Please try again.',
      );
    } finally {
      setCreating(false);
    }
  };

  /**
   * Save one step of an existing event. The override names only what this step
   * owns; `buildEventPatch` hands everything else back untouched, which is what
   * keeps a destination edit from clearing the itinerary or the guest list.
   */
  const handleSave = async () => {
    if (!event || creating) return;
    setCreating(true);
    try {
      const override: Partial<CreateEventPayload> = {};

      if (editStep === 'details') {
        override.title = title.trim() || event.title;
        override.description = description.trim();
        override.type = eventType;
        override.time_slot = buildTimeSlot(event.time_slot?.type ?? 'day_based', true);
        // No cover here: editing saves it on Done (see `saveCover` on StepDetails).
      } else if (editStep === 'location') {
        override.location = packLocation(event, locations);
      } else if (editStep === 'audience') {
        override.participants = withCrewSelection(
          event,
          crewMembers.map((m) => m.id),
          buildParticipants(),
        );
      } else if (editStep === 'itinerary') {
        // The full list: a saved row left out of it is one the user removed.
        override.sub_events = buildSubEvents();
      }

      await updateEvent(buildEventPatch(event, override) as unknown as EventResponse);
      // Only the audience step collects invites, and in edit mode the list starts
      // empty — so anything in it is someone the user just added.
      if (editStep === 'audience') await inviteGuests();
      toast.success('Changes saved');
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Couldn’t save your changes. Please try again.',
      );
    } finally {
      setCreating(false);
    }
  };

  /** The details step is the only one that can be left in an unsavable state. */
  const canSave = () => !creating && (editStep !== 'details' || !!title.trim());

  const canProceed = () => {
    // if (step === 1) return !!title.trim() && !!startDate && !exceedsMaxDays;
    if (step === 1) return !!title.trim();
    if (step >= reviewStep) return !creating;
    return true;
  };

  const goToStep = (n: number) => {
    if (n <= furthest) setStep(n);
  };

  const handleNext = () => {
    if (step < reviewStep) {
      const next = step + 1;
      setStep(next);
      setFurthest((f) => Math.max(f, next));
    } else {
      void handleCreate();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((n) => n - 1);
  };

  function resetAll() {
    planAbortRef.current?.abort();
    planAbortRef.current = null;
    tagsAbortRef.current?.abort();
    tagsAbortRef.current = null;
    tagsDraftRef.current = null;
    tagsTouchedRef.current = false;
    setTagIds([]);
    setTagsTouched(false);
    setTagsAi('idle');
    setPickerActive(false);
    setEditItinId(null);
    setEditItinText('');
    setEditTaskId(null);
    setEditTaskText('');
    setCreating(false);
    setSessionId(null);
    setPlanError(null);
    setStep(1);
    setFurthest(1);
    setTitle('');
    setDescription('');
    setMoreDetailsOpen(false);
    setPhoto(null);
    setAiCoverLoading(false);
    setAiCover(null);
    setAiCoverError(null);
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
    setLocationQuery('');
    setStops([]);
    setStartDate(null);
    setEndDate(null);
    setAllDay(false);
    setTeamOpen(false);
    setGuestsOpen(false);
    setGuests([]);
    setInviteExpiresAt(null);
    setInviteEmailTemplate('classic');
    setInviteNote('');
    setPastGuestsSelected([]);
    setTeamSelected(null);
    setExcludedCrew([]);
    setPendingPastGuests(0);
    setPendingPlan(false);
    setPendingCover(false);
    setInviteName('');
    setInviteContact('');
    setItineraryMode('');
    setItinerary([]);
    setItDate(null);
    setItEndDate(null);
    setItActivity('');
    setAiLoading(false);
    setShowItineraryDetails(false);
    setItDetailActivity('');
    setItDetailDate(null);
    setItDetailEndDate(null);
    setItDetailLocation('');
    setItDetailNotes('');
    setAiSuggestions([]);
    setAiPanelOpen(false);
    setAiPromptDraft('');
    setAiChat([]);
    setAiThinking(false);
    setAiChatOpen(true);
    setTasksMode('');
    setTasks([]);
    setShowTaskForm(false);
    setQuickTaskTitle('');
    setQuickTaskDate(null);
    setQuickTaskEndDate(null);
    setTasksAiLoading(false);
    setTasksAiSuggestions([]);
    setTasksAiPanelOpen(false);
    setTasksAiPromptDraft('');
    setTasksAiChat([]);
    setTasksAiThinking(false);
    setTasksAiChatOpen(true);
    setEventType(defaultEventType);
    setPlanTab('itinerary');
  }

  const planTabsBlock = (
    <div className={s.planTabsRow}>
      <SegmentedControl<PlanTab>
        options={[
          { value: 'itinerary', label: 'Itinerary', count: itinerary.length },
          { value: 'tasks', label: 'Tasks', count: tasks.length },
        ]}
        value={planTab}
        onChange={setPlanTab}
        fullWidth
        aria-label="Plan section"
      />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? EDIT_STEPS[editStep].title : 'Add Event'}>
      <div className={s.wizard}>
        {/* No stepper when editing: one step is reachable, so a four-segment
            progress bar would only look like a wizard that had broken. The step
            names itself in the modal title instead. */}
        {!isEdit && step < reviewStep && (
          <WizardStepper
            steps={STEPS}
            step={step}
            furthest={furthest}
            isSkippable={isSkippable}
            onGoToStep={goToStep}
          />
        )}

        <div key={step} className={s.stepBody}>
          {step === 1 && (
            <StepDetails
              title={title}
              onTitleChange={setTitle}
              description={description}
              onDescriptionChange={setDescription}
              startDate={startDate}
              endDate={endDate}
              allDay={allDay}
              onAllDayChange={handleAllDayChange}
              onDatesChange={(st, en) => {
                setStartDate(st);
                setEndDate(en);
              }}
              // exceedsMaxDays={exceedsMaxDays}
              outOfWaterRange={outOfWaterRange}
              moreDetailsOpen={moreDetailsOpen}
              onToggleMoreDetails={() => setMoreDetailsOpen((v) => !v)}
              photo={photo}
              onPhotoChange={setPhoto}
              pickerActive={pickerActive}
              onPickerActiveChange={setPickerActive}
              logoPickerRef={logoPickerRef}
              onOpenFilePicker={() => logoInputRef.current?.click()}
              aiCover={aiCover}
              onRemoveAiCover={() => setAiCover(null)}
              aiCoverLoading={aiCoverLoading}
              aiCoverError={aiCoverError}
              onGenerateAiCover={() => void generateAiCover()}
              existingCover={event && savedCover?.id === event.id ? savedCover.image : event?.image}
              saveCover={event ? (file) => saveCover(event.id, file) : undefined}
            />
          )}

          {step === 2 && (
            <StepLocation
              locationQuery={locationQuery}
              onLocationQueryChange={setLocationQuery}
              locations={stops}
              onAddLocation={addLocation}
              onRemoveLocation={removeLocation}
              onDragEnd={handleLocationDragEnd}
            />
          )}

          {step === 3 && (
            <StepAudience
              crewMembers={crewMembers}
              attendingIds={attendingCrew.map((m) => m.id)}
              creating={!teamSelected}
              lockedId={teamSelected ? null : (userId ?? null)}
              onToggleTeamMember={toggleTeam}
              onAddAllTeam={addAllTeam}
              teamOpen={teamOpen}
              onToggleTeamOpen={() => setTeamOpen((v) => !v)}
              guestsOpen={guestsOpen}
              onToggleGuestsOpen={() => setGuestsOpen((v) => !v)}
              guests={guests}
              onRemoveGuest={removeGuest}
              pastGuests={pastGuests}
              pastGuestsSelected={pastGuestsSelected}
              onTogglePastGuest={togglePastGuest}
              onClearInvite={clearInvite}
              inviteName={inviteName}
              onInviteNameChange={setInviteName}
              inviteContact={inviteContact}
              onInviteContactChange={setInviteContact}
              onSendInvitation={sendInvitation}
              eventGuests={eventGuests}
              inviteExpiresAt={inviteExpiresAt}
              onInviteExpiresAtChange={setInviteExpiresAt}
              inviteEmailTemplate={inviteEmailTemplate}
              onInviteEmailTemplateChange={setInviteEmailTemplate}
              inviteNote={inviteNote}
              onInviteNoteChange={setInviteNote}
              eventEndsAt={endDate}
              away={away}
            />
          )}

          {currentStep?.id === 'plan' && (
            <StepPlan
              planError={planError}
              aiLoading={aiLoading}
              tasksAiLoading={tasksAiLoading}
              onRetry={() => void runPlan()}
            >
              {planTab === 'itinerary' && (
                <ItineraryTab
                  // Editing opens on one tab: tasks are not saved with the event.
                  planTabs={isEdit ? null : planTabsBlock}
                  eventStart={startDate}
                  eventEnd={endDate}
                  itinerary={itinerary}
                  onRemoveItem={removeItineraryItem}
                  onAddItem={addItineraryItem}
                  itineraryMode={itineraryMode}
                  aiLoading={aiLoading}
                  itDate={itDate}
                  itEndDate={itEndDate}
                  onItDatesChange={(st, en) => {
                    setItDate(st);
                    setItEndDate(en);
                  }}
                  itActivity={itActivity}
                  onItActivityChange={setItActivity}
                  showDetails={showItineraryDetails}
                  onOpenDetails={openItineraryDetails}
                  onCloseDetails={closeItineraryDetails}
                  detailActivity={itDetailActivity}
                  onDetailActivityChange={setItDetailActivity}
                  detailDate={itDetailDate}
                  detailEndDate={itDetailEndDate}
                  onDetailDatesChange={(st, en) => {
                    setItDetailDate(st);
                    setItDetailEndDate(en);
                  }}
                  detailLocation={itDetailLocation}
                  onDetailLocationChange={setItDetailLocation}
                  detailNotes={itDetailNotes}
                  onDetailNotesChange={setItDetailNotes}
                  onSaveDetails={saveItineraryDetails}
                  aiPanelOpen={aiPanelOpen}
                  onToggleAiPanel={() => setAiPanelOpen((v) => !v)}
                  aiSuggestions={aiSuggestions}
                  aiChat={aiChat}
                  aiChatRef={aiChatRef}
                  aiThinking={aiThinking}
                  aiPromptDraft={aiPromptDraft}
                  onAiPromptDraftChange={setAiPromptDraft}
                  onSubmitAiPrompt={submitAiPrompt}
                  editItinId={editItinId}
                  editItinText={editItinText}
                  onEditItinTextChange={setEditItinText}
                  onStartEditItin={startEditItin}
                  onSaveEditItin={saveEditItin}
                  onCancelEditItin={cancelEditItin}
                  onAcceptSuggestion={acceptSuggestion}
                  onRejectSuggestion={rejectSuggestion}
                  onAcceptAllSuggestions={acceptAllSuggestions}
                />
              )}

              {planTab === 'tasks' && (
                <TasksTab
                  planTabs={planTabsBlock}
                  eventStart={startDate}
                  eventEnd={endDate}
                  tasks={tasks}
                  onRemoveTask={removeTask}
                  tasksMode={tasksMode}
                  tasksAiLoading={tasksAiLoading}
                  quickTaskTitle={quickTaskTitle}
                  onQuickTaskTitleChange={setQuickTaskTitle}
                  quickTaskDate={quickTaskDate}
                  quickTaskEndDate={quickTaskEndDate}
                  onQuickTaskDatesChange={(st, en) => {
                    setQuickTaskDate(st);
                    setQuickTaskEndDate(en);
                  }}
                  onAddQuickTask={addQuickTask}
                  showTaskForm={showTaskForm}
                  onOpenTaskForm={openTaskForm}
                  onCloseTaskForm={closeTaskForm}
                  onSaveTask={addTask}
                  aiPanelOpen={tasksAiPanelOpen}
                  onToggleAiPanel={() => setTasksAiPanelOpen((v) => !v)}
                  aiSuggestions={tasksAiSuggestions}
                  aiChat={tasksAiChat}
                  aiChatRef={tasksAiChatRef}
                  aiThinking={tasksAiThinking}
                  aiPromptDraft={tasksAiPromptDraft}
                  onAiPromptDraftChange={setTasksAiPromptDraft}
                  onSubmitAiPrompt={submitTasksAiPrompt}
                  editTaskId={editTaskId}
                  editTaskText={editTaskText}
                  onEditTaskTextChange={setEditTaskText}
                  onStartEditTask={startEditTask}
                  onSaveEditTask={saveEditTask}
                  onCancelEditTask={cancelEditTask}
                  onAcceptSuggestion={acceptTaskSuggestion}
                  onRejectSuggestion={rejectTaskSuggestion}
                  onAcceptAllSuggestions={acceptAllTaskSuggestions}
                />
              )}
            </StepPlan>
          )}

          {step >= reviewStep && (
            <StepReview
              items={reviewItems}
              noun="Event"
              tags={
                <TagPicker
                  value={tagIds}
                  onChange={changeTags}
                  suggesting={tagsAi === 'loading'}
                  emptyText="No tags"
                />
              }
            />
          )}
        </div>

        {!(currentStep?.id === 'plan' && showTaskForm) && (
          <div className={s.modalActions}>
            {isEdit ? (
              <>
                <ButtonOutlined onClick={onClose}>Cancel</ButtonOutlined>
                <span className={s.actionsSpacer} />
                <Button onClick={() => void handleSave()} disabled={!canSave()}>
                  {creating ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                {step > 1 && <ButtonOutlined onClick={handleBack}>Back</ButtonOutlined>}
                <span className={s.actionsSpacer} />
                {isSkippable && (
                  <button type="button" className={s.skipBtn} onClick={handleNext}>
                    Skip
                  </button>
                )}
                <Button onClick={handleNext} disabled={!canProceed()}>
                  {step >= reviewStep ? (creating ? 'Creating…' : 'Add Event') : 'Next'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default EventWizardModal;
